import { useQueryClient } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import React from 'react';
import { clearAgentAccess } from '../components/alliance/agent-access';
import { clearImpersonationRecents } from '../components/alliance/impersonation-search';
import { AuthContext } from '../contexts/auth-context';
import { captureError } from '../utils/capture-error';
import { markSessionExpired } from '../utils/session-expired';
import { AgentSelect } from '../types';
import {
  removeImpersonatedUser,
  storeImpersonatedUser,
} from '../utils/impersonate';

/**
 * Sign out, sign in again and Stop impersonating, shared by the user menu, the
 * impersonation search, the maintenance banner and the System Screens.
 */
export function useSessionActions() {
  // Context
  const { state, dispatch } = React.useContext(AuthContext);
  // Hooks
  const queryClient = useQueryClient();

  const impersonatedAgtNo = state.impersonatedAgent?.AgtNo;

  /**
   * Stop acting as the impersonated agent.
   *
   * Removes every cached query whose key mentions that agent's number, so
   * their data doesn't stay in memory after you leave. Queries that aren't
   * agent-scoped (/authorize, MFA, admin lists) are kept, so the app doesn't
   * reload around you.
   */
  const stopImpersonating = React.useCallback(async () => {
    dispatch({ type: 'IMPERSONATE_AGENT', payload: null });
    clearAgentAccess(impersonatedAgtNo);
    if (impersonatedAgtNo) {
      const needle = `"${impersonatedAgtNo}"`;
      queryClient.removeQueries({
        predicate: (query) => JSON.stringify(query.queryKey).includes(needle),
      });
    }
    try {
      await removeImpersonatedUser();
    } catch (error) {
      captureError({ data: { error } });
    }
  }, [dispatch, impersonatedAgtNo, queryClient]);

  /**
   * Start (or switch) impersonating an agent: drop the previous agent's
   * cached data, then store and set the new one.
   */
  const startImpersonating = React.useCallback(
    async (agent: AgentSelect) => {
      if (impersonatedAgtNo && impersonatedAgtNo !== agent.AgtNo) {
        const needle = `"${impersonatedAgtNo}"`;
        queryClient.removeQueries({
          predicate: (query) => JSON.stringify(query.queryKey).includes(needle),
        });
      }
      // Ask the API afresh; access may have changed since last time
      clearAgentAccess(impersonatedAgtNo);
      clearAgentAccess(agent.AgtNo);
      dispatch({ type: 'IMPERSONATE_AGENT', payload: agent });
      try {
        await storeImpersonatedUser({ user: agent });
      } catch (error) {
        captureError({ data: { error } });
      }
    },
    [dispatch, impersonatedAgtNo, queryClient],
  );

  /**
   * Clear stored impersonation, the recent impersonations and cached data,
   * then sign out of Cognito. A session that merely expires keeps the
   * recents. While the session has expired, the Sign In page is told so it
   * can say so.
   */
  const signOut = React.useCallback(async () => {
    if (state.tokenError) {
      markSessionExpired();
    }
    try {
      await removeImpersonatedUser();
    } catch (error) {
      captureError({ data: { error } });
    }
    clearImpersonationRecents();
    queryClient.removeQueries();
    await Auth.signOut();
  }, [queryClient, state.tokenError]);

  /**
   * Back to the Cognito Hosted UI after the session expired. Cognito may
   * sign them straight back in if its own session is still alive.
   */
  const signInAgain = React.useCallback(async () => {
    try {
      await Auth.federatedSignIn();
    } catch (error) {
      captureError({ data: { error } });
      window.location.assign('/sign-in?reason=expired');
    }
  }, []);

  return { signOut, stopImpersonating, startImpersonating, signInAgain };
}
